(() => {
  'use strict';

  const root = () => window.Shopify?.routes?.root || window.EverPhase?.root || '/';
  const cartUrl = () => `${root()}cart`;
  const drawer = () => document.querySelector('[data-cart-drawer]');
  let returnFocus = null;
  let cartRequest = 0;
  let changingCart = false;

  function money(cents, currency = document.documentElement.dataset.currency || window.EverPhase?.currency || 'USD') {
    try {
      return `${new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(Number(cents) / 100)} ${currency}`;
    } catch (_) {
      return `${(Number(cents) / 100).toFixed(2)} ${currency}`;
    }
  }

  function node(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function countCart(cart) {
    document.querySelectorAll('[data-cart-count]').forEach(element => {
      element.textContent = cart.item_count;
      element.dataset.count = cart.item_count;
      element.classList.toggle('is-empty', cart.item_count === 0);
    });
    document.dispatchEvent(new CustomEvent('everphase:cart-updated', { detail: { cart } }));
  }

  async function requestCart(path, options = {}) {
    const response = await fetch(`${root()}${path}`, {
      ...options,
      headers: { Accept: 'application/json', ...options.headers },
      credentials: 'same-origin'
    });
    const result = await response.json();
    if (!response.ok) {
      const error = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : 'Please try again or view your bag.';
      throw new Error(error);
    }
    return result;
  }

  function renderCart(cart) {
    countCart(cart);
    const panel = drawer();
    if (!panel) return;
    const items = panel.querySelector('[data-cart-items]');
    const footer = panel.querySelector('[data-cart-footer]');
    const currency = cart.currency || document.documentElement.dataset.currency || window.EverPhase?.currency || 'USD';
    const fragment = document.createDocumentFragment();
    if (!cart.items.length) {
      const empty = node('div', 'cart-drawer__empty');
      empty.append(node('h3', '', 'A little room for what’s next.'), node('p', '', 'Your bag is currently empty.'));
      const shop = node('a', 'button', 'Explore the collection');
      shop.href = `${root()}collections/all`;
      empty.append(shop);
      fragment.append(empty);
    }
    cart.items.forEach((item, index) => {
      const article = node('article', 'cart-drawer__item');
      article.dataset.cartKey = item.key;
      const imageLink = node('a', 'cart-drawer__image');
      imageLink.href = item.url;
      if (item.featured_image?.url || item.image) {
        const image = node('img');
        image.src = item.featured_image?.url || item.image;
        image.alt = item.featured_image?.alt || item.product_title || item.title;
        image.width = 120;
        image.height = 145;
        image.loading = 'lazy';
        imageLink.append(image);
      }
      const content = node('div');
      const heading = node('h3');
      const link = node('a', '', item.product_title || item.title);
      link.href = item.url;
      heading.append(link);
      content.append(heading);
      if (item.variant_title && item.variant_title !== 'Default Title') content.append(node('p', '', item.variant_title));
      if (item.selling_plan_allocation?.selling_plan?.name) content.append(node('p', '', item.selling_plan_allocation.selling_plan.name));
      Object.entries(item.properties || {}).forEach(([key, value]) => {
        if (!key.startsWith('_') && value) content.append(node('p', '', `${key}: ${value}`));
      });
      (item.line_level_discount_allocations || []).forEach(discount => content.append(node('p', 'cart-discount', discount.discount_application.title)));
      const controls = node('div', 'cart-drawer__item-controls');
      const quantity = node('div', 'quantity-control');
      const minus = node('button', '', '−');
      minus.type = 'button';
      minus.dataset.quantityChange = '-1';
      minus.setAttribute('aria-label', `Decrease quantity for ${item.product_title || item.title}`);
      const input = node('input');
      input.type = 'number';
      input.min = String(item.quantity_rule?.min || 1);
      input.step = String(item.quantity_rule?.increment || 1);
      if (item.quantity_rule?.max) input.max = String(item.quantity_rule.max);
      input.value = item.quantity;
      input.inputMode = 'numeric';
      input.dataset.cartQuantity = item.key;
      input.id = `DrawerQuantity-${index}`;
      input.setAttribute('aria-label', `Quantity for ${item.product_title || item.title}`);
      const plus = node('button', '', '+');
      plus.type = 'button';
      plus.dataset.quantityChange = '1';
      plus.setAttribute('aria-label', `Increase quantity for ${item.product_title || item.title}`);
      quantity.append(minus, input, plus);
      controls.append(quantity, node('span', 'cart-drawer__line-price', money(item.final_line_price, currency)));
      content.append(controls);
      const remove = node('button', 'cart-drawer__remove', 'Remove');
      remove.type = 'button';
      remove.dataset.cartRemove = item.key;
      remove.setAttribute('aria-label', `Remove ${item.product_title || item.title}`);
      content.append(remove);
      article.append(imageLink, content);
      fragment.append(article);
    });
    items.replaceChildren(fragment);
    footer.hidden = !cart.items.length;
    panel.querySelector('[data-cart-subtotal]').textContent = money(cart.total_price, currency);
  }

  async function refreshCart() {
    const request = ++cartRequest;
    const cart = await requestCart('cart.js');
    if (request === cartRequest) renderCart(cart);
    return cart;
  }

  async function openDrawer(opener, shouldRefresh = true) {
    const panel = drawer();
    if (!panel || typeof panel.showModal !== 'function') {
      window.location.assign(cartUrl());
      return;
    }
    if (!panel.open) {
      returnFocus = opener || document.activeElement;
      panel.showModal();
      document.documentElement.classList.add('cart-is-open');
      panel.querySelector('[data-cart-close]').focus();
    }
    if (!shouldRefresh) return;
    panel.setAttribute('aria-busy', 'true');
    const status = panel.querySelector('[data-cart-status]');
    status.textContent = 'Loading your bag…';
    try {
      await refreshCart();
      status.textContent = '';
    } catch (_) {
      status.textContent = 'Your bag couldn’t load. Please use “View bag” below to continue.';
      panel.querySelector('[data-cart-footer]').hidden = false;
    } finally {
      panel.removeAttribute('aria-busy');
    }
  }

  function closeDrawer() {
    const panel = drawer();
    if (!panel?.open) return;
    panel.close();
    document.documentElement.classList.remove('cart-is-open');
    if (returnFocus?.isConnected) returnFocus.focus();
  }

  async function changeCart(key, quantity, source) {
    if (changingCart) return;
    const panel = drawer();
    changingCart = true;
    panel.setAttribute('aria-busy', 'true');
    const status = panel.querySelector('[data-cart-status]');
    const previousIndex = [...panel.querySelectorAll('[data-cart-key]')].findIndex(item => item.dataset.cartKey === key);
    const restoreToInput = source?.matches('[data-cart-quantity]');
    status.textContent = 'Updating your bag…';
    try {
      ++cartRequest;
      const cart = await requestCart('cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
      renderCart(cart);
      status.textContent = quantity === 0 ? 'Item removed from your bag.' : 'Your bag has been updated.';
      const remaining = [...panel.querySelectorAll('[data-cart-key]')];
      const replacement = remaining.find(item => item.dataset.cartKey === key) || remaining[Math.min(previousIndex, remaining.length - 1)];
      const focusTarget = replacement?.querySelector(restoreToInput ? '[data-cart-quantity]' : '[data-cart-remove]') || panel.querySelector('[data-cart-close]');
      focusTarget?.focus();
    } catch (error) {
      status.textContent = error.message || 'Your bag couldn’t be updated. Please try again.';
      // Reconcile the quantity if Shopify rejected a stock or quantity-rule change.
      try { await refreshCart(); } catch (_) { /* The visible cart link remains available. */ }
    } finally {
      panel.removeAttribute('aria-busy');
      changingCart = false;
    }
  }

  function setGallery(section, mediaId) {
    if (!mediaId) return;
    const media = [...section.querySelectorAll('[data-media-id]')];
    if (!media.some(element => element.dataset.mediaId === String(mediaId))) return;
    media.forEach(element => {
      element.hidden = element.dataset.mediaId !== String(mediaId);
      if (element.hidden) element.querySelectorAll('video').forEach(video => video.pause());
    });
    section.querySelectorAll('[data-gallery-select]').forEach(element => {
      const active = element.dataset.gallerySelect === String(mediaId);
      element.classList.toggle('is-active', active);
      if (active) element.setAttribute('aria-current', 'true');
      else element.removeAttribute('aria-current');
    });
  }

  function updateVariant(section, updateUrl = true) {
    const script = section.querySelector('[data-product-json]');
    const form = section.querySelector('[data-product-form]');
    if (!script || !form) return;
    let product;
    try { product = JSON.parse(script.textContent); } catch (_) { return; }
    const variant = product.variants.find(item => String(item.id) === form.elements.id.value);
    if (!variant) return;
    const planSelect = form.querySelector('[data-selling-plan]');
    if (planSelect) {
      [...planSelect.options].forEach(option => {
        option.disabled = !!option.value && !variant.selling_plan_allocations?.some(allocation => String(allocation.selling_plan_id) === option.value);
      });
      if (planSelect.selectedOptions[0]?.disabled) planSelect.value = [...planSelect.options].find(option => !option.disabled)?.value || '';
    }
    const selectedPlan = planSelect?.value;
    const allocation = variant.selling_plan_allocations?.find(item => String(item.selling_plan_id) === selectedPlan);
    const price = allocation?.price ?? variant.price;
    const comparePrice = allocation?.compare_at_price ?? variant.compare_at_price;
    section.querySelectorAll('[data-product-price]').forEach(element => element.textContent = money(price, section.dataset.currency));
    section.querySelectorAll('[data-product-compare]').forEach(element => {
      element.hidden = !comparePrice || comparePrice <= price;
      element.textContent = money(comparePrice || 0, section.dataset.currency);
    });
    const purchasable = variant.available && (!product.requires_selling_plan || !!allocation);
    section.querySelectorAll('[data-add-to-cart]').forEach(button => {
      button.disabled = !purchasable;
      button.textContent = !variant.available ? 'Sold out' : purchasable ? 'Add to bag' : 'Unavailable';
    });
    section.querySelectorAll('[data-product-unit]').forEach(element => {
      element.hidden = !variant.unit_price_measurement;
      if (variant.unit_price_measurement) {
        const unit = variant.unit_price_measurement;
        element.textContent = `${money(allocation?.unit_price ?? variant.unit_price, section.dataset.currency)} / ${unit.reference_value === 1 ? '' : unit.reference_value}${unit.reference_unit}`;
      }
    });
    const quantity = form.elements.quantity;
    if (quantity && variant.quantity_rule) {
      quantity.min = variant.quantity_rule.min || 1;
      quantity.step = variant.quantity_rule.increment || 1;
      if (variant.quantity_rule.max) quantity.max = variant.quantity_rule.max;
      else quantity.removeAttribute('max');
      const minimum = Number(quantity.min);
      const increment = Number(quantity.step);
      const rounded = minimum + Math.max(0, Math.round((Number(quantity.value) - minimum) / increment)) * increment;
      const upper = quantity.max ? minimum + Math.floor((Number(quantity.max) - minimum) / increment) * increment : Infinity;
      quantity.value = Math.max(minimum, Math.min(rounded, upper));
    }
    setGallery(section, variant.featured_media?.id);
    if (updateUrl && window.location.pathname.includes('/products/')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-cart-open]');
    if (opener) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      openDrawer(opener);
      return;
    }
    if (event.target.closest('[data-cart-close]')) { closeDrawer(); return; }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { changeCart(remove.dataset.cartRemove, 0, remove); return; }
    const quantityButton = event.target.closest('[data-quantity-change]');
    if (quantityButton) {
      const input = quantityButton.closest('.quantity-control')?.querySelector('input');
      if (!input || input.disabled || (drawer()?.contains(input) && changingCart)) return;
      const min = Number(input.min) || 0;
      const max = input.max ? Number(input.max) : Infinity;
      const increment = Number(input.step) || 1;
      const value = Number(input.value) || min;
      const next = Math.max(min, Math.min(max, value + Number(quantityButton.dataset.quantityChange) * increment));
      if (next !== value) {
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }
    const thumbnail = event.target.closest('[data-gallery-select]');
    if (thumbnail && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      setGallery(thumbnail.closest('[data-product-section]'), thumbnail.dataset.gallerySelect);
      return;
    }
    const detailsLink = event.target.closest('[data-open-details]');
    if (detailsLink) {
      const hash = new URL(detailsLink.href, window.location.href).hash;
      const anchor = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
      const target = anchor?.tagName === 'DETAILS' ? anchor : anchor?.querySelector('details');
      if (target) {
        event.preventDefault();
        target.open = true;
        target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
        target.querySelector('summary')?.focus({ preventScroll: true });
      }
    }
  });

  document.addEventListener('change', event => {
    if (event.target.matches('[data-variant-select], [data-selling-plan]')) updateVariant(event.target.closest('[data-product-section]'));
    if (event.target.matches('[data-cart-quantity]')) {
      const value = Number(event.target.value);
      if (!Number.isInteger(value) || value < 0 || !event.target.checkValidity()) { event.target.reportValidity(); return; }
      changeCart(event.target.dataset.cartQuantity, value, event.target);
    }
  });

  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-product-form]');
    if (!form || !window.fetch || !window.FormData) return;
    event.preventDefault();
    if (form.dataset.submitting === 'true') return;
    form.dataset.submitting = 'true';
    const section = form.closest('[data-product-section]');
    const buttons = [...section.querySelectorAll('[data-add-to-cart]')];
    const message = form.querySelector('[data-product-message]');
    const opener = event.submitter || form.querySelector('[data-add-to-cart]');
    if (message) { message.hidden = true; message.textContent = ''; delete message.dataset.state; }
    buttons.forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); button.textContent = 'Adding…'; });
    let added = false;
    try {
      await requestCart('cart/add.js', { method: 'POST', body: new FormData(form) });
      added = true;
      await openDrawer(opener);
    } catch (error) {
      if (message) { message.hidden = false; message.dataset.state = 'error'; message.textContent = error.message || 'We couldn’t add this item. Please try again.'; }
    } finally {
      form.dataset.submitting = 'false';
      buttons.forEach(button => { button.removeAttribute('aria-busy'); button.disabled = false; button.textContent = 'Add to bag'; });
      updateVariant(section, false);
      if (added && message) { message.hidden = false; message.dataset.state = 'success'; message.textContent = 'Added to your bag.'; }
    }
  });

  function bindDrawer() {
    const panel = drawer();
    if (!panel || panel.dataset.bound) return;
    panel.dataset.bound = 'true';
    panel.addEventListener('cancel', event => { event.preventDefault(); closeDrawer(); });
    panel.addEventListener('close', () => document.documentElement.classList.remove('cart-is-open'));
    panel.addEventListener('click', event => {
      if (event.target !== panel) return;
      const bounds = panel.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDrawer();
    });
  }

  function initializeProducts() {
    document.querySelectorAll('[data-product-section]').forEach(section => updateVariant(section, false));
    if (window.location.hash) {
      const anchor = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
      const details = anchor?.tagName === 'DETAILS' ? anchor : anchor?.querySelector('details');
      if (details) details.open = true;
    }
  }

  bindDrawer();
  initializeProducts();
  document.addEventListener('DOMContentLoaded', () => { bindDrawer(); initializeProducts(); });
  document.addEventListener('shopify:section:load', () => { bindDrawer(); initializeProducts(); });
  window.addEventListener('hashchange', initializeProducts);
  window.addEventListener('pageshow', event => { if (event.persisted) refreshCart().catch(() => {}); });
})();
